/**
 * Utility functions for exporting data from the application
 */
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Add the autotable method to jsPDF
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

/**
 * Exports data as a downloadable JSON file
 * @param data - Any JavaScript object or array to export
 * @param filename - Name of the file (without extension)
 */
export const exportToJson = (data: any, filename: string): void => {
  // Format the data as pretty JSON with indentation
  const jsonString = JSON.stringify(data, null, 2);
  
  // Create a blob with the JSON content
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  // Create a temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename.replace(/\s+/g, '_')}.json`;
  
  // Trigger the download
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exports data as a downloadable CSV file
 * @param headers - Array of column headers
 * @param rows - Array of arrays containing row data
 * @param filename - Name of the file (without extension)
 */
export const exportToCsv = (headers: string[], rows: (string | number)[][], filename: string): void => {
  // Format the CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      // Handle special characters and commas in the data
      const cellStr = String(cell);
      return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')
        ? `"${cellStr.replace(/"/g, '""')}"`
        : cellStr;
    }).join(','))
  ].join('\n');
  
  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Create a temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename.replace(/\s+/g, '_')}.csv`;
  
  // Trigger the download
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Format a date object as a string suitable for filenames
 * @param date - Date object to format
 * @returns Formatted date string (YYYY-MM-DD)
 */
export const formatDateForFilename = (date: Date = new Date()): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Export chart or any DOM element as PNG image
 * @param elementRef - Reference to the DOM element to export
 * @param filename - Name of the file (without extension)
 */
export const exportToPng = async (
  elementRef: HTMLElement | null,
  filename: string
): Promise<string | null> => {
  if (!elementRef) return null;
  
  try {
    // Convert the element to a PNG data URL
    const dataUrl = await toPng(elementRef, {
      quality: 0.95,
      backgroundColor: '#fff'
    });
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.download = `${filename.replace(/\s+/g, '_')}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    
    return dataUrl;
  } catch (error) {
    console.error('Error exporting to PNG:', error);
    return null;
  }
};

/**
 * Export data as a PDF table
 * @param headers - Column headers
 * @param data - Table data as array of arrays
 * @param filename - Name of the file (without extension)
 * @param title - Optional title for the PDF
 */
export const exportToPdf = (
  headers: string[], 
  data: (string | number)[][], 
  filename: string,
  title?: string
): void => {
  try {
    // Create a new PDF document
    const doc = new jsPDF();
    
    // Add title if provided
    if (title) {
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
    }
    
    // Add timestamp
    const timestamp = `Generated on: ${new Date().toLocaleString()}`;
    doc.setFontSize(9);
    doc.text(timestamp, 14, title ? 22 : 15);
    doc.setFontSize(10);
    
    // Add the table
    doc.autoTable({
      head: [headers],
      body: data,
      startY: title ? 25 : 20,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      }
    });
    
    // Save the PDF
    doc.save(`${filename.replace(/\s+/g, '_')}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
  }
};

/**
 * Export chart or visualization as PDF
 * @param elementRef - Reference to the DOM element containing the chart
 * @param filename - Name of the file (without extension)
 * @param title - Optional title for the PDF
 * @param additionalData - Optional additional data to include in the PDF
 */
export const exportChartToPdf = async (
  elementRef: HTMLElement | null,
  filename: string,
  title?: string,
  additionalData?: { headers: string[], data: (string | number)[][] }
): Promise<void> => {
  if (!elementRef) return;
  
  try {
    // First convert the chart to an image
    const imageDataUrl = await toPng(elementRef, {
      quality: 0.95,
      backgroundColor: '#fff'
    });
    
    // Create a new PDF document
    const doc = new jsPDF();
    
    // Add title if provided
    if (title) {
      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.setFontSize(10);
    }
    
    // Add timestamp
    const timestamp = `Generated on: ${new Date().toLocaleString()}`;
    doc.setFontSize(9);
    doc.text(timestamp, 14, title ? 22 : 15);
    doc.setFontSize(10);
    
    // Calculate the image dimensions to fit on the page
    const imgWidth = doc.internal.pageSize.getWidth() - 28; // 14pt margin on each side
    const imgHeight = (elementRef.offsetHeight * imgWidth) / elementRef.offsetWidth;
    
    // Add the chart image
    doc.addImage(imageDataUrl, 'PNG', 14, title ? 25 : 20, imgWidth, imgHeight);
    
    // Add additional data table if provided
    if (additionalData) {
      doc.autoTable({
        head: [additionalData.headers],
        body: additionalData.data,
        startY: (title ? 25 : 20) + imgHeight + 10,
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240]
        }
      });
    }
    
    // Save the PDF
    doc.save(`${filename.replace(/\s+/g, '_')}.pdf`);
  } catch (error) {
    console.error('Error exporting chart to PDF:', error);
  }
};

/**
 * Export a chart to PNG with more flexible typing that handles null refs
 * @param chartRef - Reference to the chart component, possibly null
 * @param filename - Name for the exported file
 */
export const safeExportChartToPng = async (
  chartRef: React.RefObject<HTMLDivElement | null>,
  filename: string
): Promise<string | null> => {
  if (!chartRef.current) return null;
  
  try {
    // Convert the chart to a PNG data URL
    const dataUrl = await toPng(chartRef.current, {
      quality: 0.95,
      backgroundColor: '#fff'
    });
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.download = `${filename.replace(/\s+/g, '_')}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return dataUrl;
  } catch (error) {
    console.error('Error exporting chart to PNG:', error);
    return null;
  }
};

export default {
  exportToJson,
  exportToCsv,
  exportToPng,
  exportToPdf,
  exportChartToPdf,
  formatDateForFilename
};
